import { create } from 'zustand';
import { useApplicationStore } from './application';
import { useInsuranceStore } from './insurance';
import { useOnboardingStore } from '@features/onboarding/store/onboarding';
import {
  resolveUserState,
  isPlanAvailableInState,
} from '@utils/planAvailability';

interface ForbiddenPlansState {
  isModalOpen: boolean;
  isProcessing: boolean;
  error: string | null;
  hasChecked: boolean;

  setModalOpen: (isOpen: boolean) => void;
  resolveForbiddenPlans: () => Promise<void>;
  checkForbiddenPlans: () => Promise<boolean>;
}

const removeForbiddenPlansFromApplication = async () => {
  const { currentApplicationId, applications } = useApplicationStore.getState();
  const fullPlans = useInsuranceStore.getState().plans;
  const data = useOnboardingStore.getState().data;
  const app = applications[0];

  if (!currentApplicationId || !app || !fullPlans.length) {
    throw new Error('No application ID found');
  }

  const appAddress = app.address as
    | { zipcode?: string; state?: string }
    | undefined;
  const userState =
    resolveUserState(appAddress?.zipcode, appAddress?.state) ||
    resolveUserState(data.address?.zipcode, data.address?.state);

  if (!userState) {
    throw new Error('Unable to determine user state');
  }

  const validPlans = (app.plans || []).filter((appPlan) => {
    const fullPlan = fullPlans.find((p) => p.id === appPlan.id);
    return !fullPlan || isPlanAvailableInState(fullPlan, userState);
  });

  await useApplicationStore
    .getState()
    .updateApplicationById(currentApplicationId, {
      ...app,
      plans: validPlans,
    });
};

export const useForbiddenPlansStore = create<ForbiddenPlansState>(
  (set, get) => ({
    isModalOpen: false,
    isProcessing: false,
    error: null,
    hasChecked: false,

    setModalOpen: (isOpen: boolean) => {
      set({ isModalOpen: isOpen });
    },

    resolveForbiddenPlans: async () => {
      set({ isProcessing: true, error: null });
      try {
        await removeForbiddenPlansFromApplication();
        await useApplicationStore.getState().updateTierFromApplicationState();
        await useApplicationStore.getState().getCurrentApplication();
        set({ isModalOpen: false, isProcessing: false, hasChecked: false });
        window.location.reload();
      } catch (error: any) {
        const errorMessage =
          error?.response?.data?.details?.message ||
          'Failed to update unavailable plans';
        console.error('Failed to update unavailable plans:', error);
        set({ error: errorMessage, isProcessing: false });
        throw error;
      }
    },

    checkForbiddenPlans: async () => {
      if (get().hasChecked) return get().isModalOpen;

      const {
        hasCompletedApplication,
        originalApiPlans,
        signedAt,
        currentApplicationId,
        applications,
      } = useApplicationStore.getState();
      const fullPlans = useInsuranceStore.getState().plans;
      const data = useOnboardingStore.getState().data;

      // Only act on a pending / in-progress application (status 0).
      if (hasCompletedApplication) return false;
      if (
        !currentApplicationId ||
        !originalApiPlans.length ||
        !fullPlans.length
      ) {
        return false;
      }

      const app = applications[0];
      const appAddress = app?.address as
        | { zipcode?: string; state?: string }
        | undefined;
      const userState =
        resolveUserState(appAddress?.zipcode, appAddress?.state) ||
        resolveUserState(data.address?.zipcode, data.address?.state);

      // Can't determine the user's state yet -> retry later, don't act.
      if (!userState) return false;

      // We have everything needed — run the check once per page load.
      set({ hasChecked: true });

      const forbidden = originalApiPlans.filter((appPlan) => {
        const fullPlan = fullPlans.find((p) => p.id === appPlan.id);
        return fullPlan && !isPlanAvailableInState(fullPlan, userState);
      });

      if (!forbidden.length) return false;

      console.log('[ForbiddenPlans] Forbidden plans found in application:', {
        userState,
        forbidden: forbidden.map((p) => p.id),
        signed: !!signedAt,
      });

      if (signedAt) {
        set({ isModalOpen: true });
        return true;
      }

      try {
        await removeForbiddenPlansFromApplication();
      } catch (error) {
        console.error('Failed to erase forbidden plans:', error);
      }
      return false;
    },
  })
);
